import React, { useEffect, useState } from 'react'

import './DashboardSharingControls.css'
import './DashboardVersionHistoryModal.css'
import { formatDate } from './Utilities'
import {
  listDashboardVersions,
  previewDashboardVersion,
  restoreDashboardVersion,
} from '../services/dashboardVersionApi'

// Ghost-box preview grid — same 12-column model react-grid-layout uses on the
// board itself (see DASHBOARD_GRID_PROPS in DashboardDetailsScreen). Boxes
// are positioned with plain percentages inside a container whose
// aspect-ratio is locked to `GRID_COLS / rows`, which is enough to make a
// percentage of width and a percentage of height land on the same physical
// size as a percentage of columns and rows — i.e. square grid cells — with
// no JS measuring or ResizeObserver required.
const GRID_COLS = 12

const widgetIcon = (widgetType) => (
  widgetType === 'text' ? 'fa-align-left'
    : widgetType === 'indicator' ? 'fa-gauge-high'
      : widgetType === 'series' ? 'fa-chart-line'
        : 'fa-square'
)

const summaryParts = (summary) => {
  if (!summary) return []
  const parts = []
  if (summary.added) parts.push(`+${summary.added} added`)
  if (summary.removed) parts.push(`-${summary.removed} removed`)
  if (summary.moved) parts.push(`${summary.moved} moved`)
  if (summary.content_edited) parts.push(`${summary.content_edited} content edited`)
  if (parts.length === 0) parts.push('No layout changes')
  return parts
}

function DashboardVersionHistoryModal({
  code,
  access,
  lockSessionId,
  lockHeld,
  acquireLock,
  onRestored,
  closeCallback,
}) {
  const [versions, setVersions] = useState([])
  const [versionsLoading, setVersionsLoading] = useState(true)
  const [versionsError, setVersionsError] = useState(null)

  const [selected, setSelected] = useState(null)
  const [preview, setPreview] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState(null)

  // 'idle' | 'confirm' | 'restoring'
  const [restoreStep, setRestoreStep] = useState('idle')
  const [restoreError, setRestoreError] = useState(null)
  const [lockWaitMessage, setLockWaitMessage] = useState(null)

  useEffect(() => {
    let active = true
    setVersionsLoading(true)
    setVersionsError(null)
    listDashboardVersions(code, access)
      .then(data => {
        if (!active) return
        setVersions(data)
        if (data.length > 0) setSelected(data[0].version)
      })
      .catch(() => { if (active) setVersionsError('Unable to load version history.') })
      .finally(() => { if (active) setVersionsLoading(false) })
    return () => { active = false }
  }, [code, access])

  useEffect(() => {
    if (selected == null) return undefined
    let active = true
    setPreviewLoading(true)
    setPreviewError(null)
    setPreview(null)
    previewDashboardVersion(code, selected, access)
      .then(data => { if (active) setPreview(data) })
      .catch(() => { if (active) setPreviewError('Unable to load this version’s preview.') })
      .finally(() => { if (active) setPreviewLoading(false) })
    return () => { active = false }
  }, [code, selected, access])

  useEffect(() => {
    const escHandler = (event) => { if (event.key === 'Escape') closeCallback() }
    document.addEventListener('keydown', escHandler)
    return () => document.removeEventListener('keydown', escHandler)
  }, [closeCallback])

  const newestVersion = versions.length > 0 ? versions[0].version : null
  const isCurrentVersion = selected != null && selected === newestVersion

  const openConfirm = () => {
    setRestoreError(null)
    setLockWaitMessage(null)
    setRestoreStep('confirm')
  }

  const cancelConfirm = () => {
    setRestoreStep('idle')
    setRestoreError(null)
    setLockWaitMessage(null)
  }

  const confirmRestore = async () => {
    setRestoreStep('restoring')
    setRestoreError(null)
    setLockWaitMessage(null)

    if (!lockHeld) {
      const lockResult = await acquireLock()
      if (!lockResult.ok) {
        setRestoreStep('confirm')
        setLockWaitMessage(
          lockResult.holder
            ? `${lockResult.holder.display_name || 'Someone'} is currently editing this dashboard. Restoring isn’t available until they finish or leave edit mode.`
            : 'Unable to acquire the edit lock right now. Please try again.'
        )
        return
      }
    }

    try {
      const result = await restoreDashboardVersion(code, selected, access, lockSessionId)
      setRestoreStep('idle')
      // The list just changed (restore always writes a new version), so this
      // closes rather than show a now-stale list. onRestored is responsible
      // for surfacing a visible confirmation on the screen behind this modal
      // — a toast rendered here would unmount with the modal before anyone
      // could read it. `targetVersion` is not part of the API response; it's
      // added here so the caller can say which version it restored to.
      onRestored({ ...result, targetVersion: selected })
      closeCallback()
    } catch (error) {
      setRestoreStep('confirm')
      if (error.response?.status === 409) {
        setRestoreError('This dashboard changed underneath the restore (the edit lock was lost). Please reopen version history and try again.')
      } else {
        setRestoreError('Unable to restore this version. Please try again.')
      }
    }
  }

  const previewWidgets = preview?.widgets || []
  const previewRows = Math.max(1, ...previewWidgets.map(w => w.y + w.h), 1)

  return (
    <div className='modal-container dashboard-version-modal-container' onClick={closeCallback}>
      <div
        className='modal-window dashboard-version-modal-window'
        role='dialog'
        aria-modal='true'
        aria-label='Dashboard version history'
        onClick={e => e.stopPropagation()}
      >
        <div className='dashboard-version-modal-header'>
          <h2 className='dashboard-version-modal-title'>Version history</h2>
          <button type='button' className='dashboard-version-close-button' onClick={closeCallback} aria-label='Close version history'>
            <i className='fa-solid fa-xmark' aria-hidden='true' />
          </button>
        </div>

        <p className='dashboard-version-hint'>
          Layout and which widgets existed are always restored exactly. Content edited before the oldest version
          kept here can’t be recovered — only the newest {versions.length > 15 ? 15 : versions.length || 15} versions
          are retained.
        </p>

        <div className='dashboard-version-modal-body'>
          <div className='dashboard-version-list-panel' aria-label='Versions'>
            {versionsLoading ? (
              <div className='dashboard-version-loader' role='status' aria-label='Loading version history'>
                <span className='dashboard-sharing-spinner' aria-hidden='true' />
              </div>
            ) : versionsError ? (
              <div className='dashboard-sharing-inline-error' role='alert'>{versionsError}</div>
            ) : versions.length === 0 ? (
              <p className='dashboard-sharing-empty'>No saved versions yet.</p>
            ) : (
              <ul className='dashboard-version-list'>
                {versions.map(version => (
                  <li key={version.version}>
                    <button
                      type='button'
                      className={`dashboard-version-list-item ${selected === version.version ? 'dashboard-version-list-item-active' : ''}`}
                      onClick={() => setSelected(version.version)}
                    >
                      <div className='dashboard-version-list-item-top'>
                        <span className='dashboard-version-number'>v{version.version}</span>
                        {version.restored_from != null && (
                          <span className='dashboard-version-restore-badge' title={version.label || `Restored from v${version.restored_from}`}>
                            <i className='fa-solid fa-clock-rotate-left' aria-hidden='true' />
                            restore point
                          </span>
                        )}
                      </div>
                      <div className='dashboard-version-list-item-meta'>
                        {formatDate(version.created_at)}{version.author ? ` · ${version.author}` : ''}
                      </div>
                      <div className='dashboard-version-list-item-summary'>
                        {summaryParts(version.summary).join(', ')}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className='dashboard-version-preview-panel' aria-label='Selected version preview'>
            {previewLoading ? (
              <div className='dashboard-version-loader' role='status' aria-label='Loading preview'>
                <span className='dashboard-sharing-spinner' aria-hidden='true' />
              </div>
            ) : previewError ? (
              <div className='dashboard-sharing-inline-error' role='alert'>{previewError}</div>
            ) : !preview ? (
              <p className='dashboard-sharing-empty'>Select a version to preview it.</p>
            ) : previewWidgets.length === 0 ? (
              <p className='dashboard-sharing-empty'>This version has no widgets.</p>
            ) : (
              <>
                <p className='dashboard-version-preview-caption'>
                  {previewWidgets.length} widget(s) — shown as arrangement and type only, never live content.
                </p>
                <div
                  className='dashboard-version-ghost-grid'
                  style={{ aspectRatio: `${GRID_COLS} / ${previewRows}` }}
                >
                  {previewWidgets.map(widget => (
                    <div
                      key={widget.uid}
                      className={`dashboard-version-ghost-box dashboard-version-ghost-box-${widget.widget_type || 'default'}`}
                      style={{
                        left: `${(widget.x / GRID_COLS) * 100}%`,
                        width: `${(widget.w / GRID_COLS) * 100}%`,
                        top: `${(widget.y / previewRows) * 100}%`,
                        height: `${(widget.h / previewRows) * 100}%`,
                      }}
                      title={widget.title}
                    >
                      <i className={`fa-solid ${widgetIcon(widget.widget_type)}`} aria-hidden='true' />
                      <span className='dashboard-version-ghost-box-title'>{widget.title}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className='dashboard-version-modal-footer'>
          {restoreStep === 'confirm' ? (
            <div className='dashboard-version-confirm'>
              <p className='dashboard-version-confirm-text'>
                Restoring will move the dashboard forward to match version {selected}: its arrangement and which
                widgets exist will be applied exactly. Content edited before this history’s oldest retained
                version keeps its current text/values (see the note above). The dashboard’s current state is
                itself kept as a new version first, so this can be undone by restoring the version before it.
              </p>
              {lockWaitMessage && (
                <div className='dashboard-sharing-inline-error' role='alert'>{lockWaitMessage}</div>
              )}
              {restoreError && (
                <div className='dashboard-sharing-inline-error' role='alert'>{restoreError}</div>
              )}
              <div className='dashboard-version-confirm-actions'>
                <button type='button' className='dashboard-sharing-button dashboard-sharing-button-secondary' onClick={cancelConfirm} disabled={restoreStep === 'restoring'}>
                  Cancel
                </button>
                <button type='button' className='dashboard-sharing-button dashboard-sharing-button-primary' onClick={confirmRestore} disabled={restoreStep === 'restoring'}>
                  {restoreStep === 'restoring' ? (
                    <span className='dashboard-sharing-spinner' role='status' aria-label='Restoring in progress' />
                  ) : (
                    <i className='fa-solid fa-clock-rotate-left' aria-hidden='true' />
                  )}
                  <span>Restore version {selected}</span>
                </button>
              </div>
            </div>
          ) : (
            <div className='dashboard-version-footer-actions'>
              {isCurrentVersion && selected != null && (
                <span className='dashboard-version-current-note'>This is the current version.</span>
              )}
              <button
                type='button'
                className='dashboard-sharing-button dashboard-sharing-button-primary'
                onClick={openConfirm}
                disabled={selected == null || isCurrentVersion || versionsLoading}
              >
                <i className='fa-solid fa-clock-rotate-left' aria-hidden='true' />
                <span>Restore this version</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DashboardVersionHistoryModal
